// F9 · Login de ADMINISTRACIÓN contra el backend propio (port del cliente de PR #162).
//
// Va aparte de `hubPost` porque una autenticación necesita distinguir cosas que una llamada de
// datos no: **"el servidor me rechazó" no es lo mismo que "el servidor no está"**, y `hubPost`
// colapsa las dos en un `Error` con texto. Acá el estado se devuelve explícito.
//
// 🔑 EL INTERLOCK, que es lo que hace seguro este cambio:
//   · Sin admin provisionado en `admin_users`, el backend responde **404 `migrado:false`** → acá
//     `disponible:false` → `auth.js` conserva su login de siempre. Nada cambia.
//   · Con admin provisionado, el backend es la AUTORIDAD: si contesta (200 o 401), el atajo de la
//     clave horneada en el bundle **deja de correr**. Si no se apagara, alguien con la clave vieja
//     —que es pública, porque `REACT_APP_*` se hornea en el build— entraría igual con un token sin
//     firmar, que es exactamente el agujero que este trabajo cierra.
//
// O sea: provisionar al admin es UNA sola llave que mueve el guard del servidor y el login del
// cliente a la vez. No existe el estado "mitad y mitad".

import { hubApiActivo, hubApiBaseUrl } from './hub';

const KEY_TOKEN = 'sh:admin:jwt';
const KEY_EXP = 'sh:admin:exp';
const KEY_ROLE = 'sh:admin:role';

const guardar = (k, v) => { try { localStorage.setItem(k, String(v)); } catch (_) { /* modo privado */ } };
const leer = (k) => { try { return localStorage.getItem(k) || ''; } catch (_) { return ''; } };
const borrar = (k) => { try { localStorage.removeItem(k); } catch (_) { /* ídem */ } };

/** Solo el backend propio (único canal). */
function destinos() {
  const out = [];
  if (hubApiActivo() && hubApiBaseUrl()) out.push(hubApiBaseUrl());
  return out;
}

/**
 * Intenta el login de administración.
 *
 * @returns {Promise<{disponible:boolean, ok?:boolean, token?:string, role?:string, email?:string,
 *                    exp?:number, error?:string, status?:number}>}
 *   · `disponible:false` → el mecanismo no está en servicio (404, canal caído, sin backend).
 *     El llamador sigue con su flujo de siempre.
 *   · `disponible:true, ok:false` → el servidor RECHAZÓ. Es autoritativo: no hay que buscar otra
 *     puerta.
 */
export async function loginAdminRemoto(email, password) {
  if (typeof fetch !== 'function') return { disponible: false };

  for (const base of destinos()) {
    let res;
    let texto;
    try {
      res = await fetch(`${base}/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      texto = await res.text();
    } catch (_) {
      continue; // este destino no responde → se prueba el siguiente
    }

    // 404 = no migrado / no provisionado · 5xx de gateway = no hay servicio detrás.
    if (res.status === 404 || res.status === 502 || res.status === 503 || res.status === 504) continue;

    let datos;
    try { datos = texto ? JSON.parse(texto) : {}; } catch (_) { continue; } // HTML del SPA → no es la API

    // Clave correcta pero la cuenta exige segundo factor: el backend YA mandó el código y NO
    // devuelve token. El llamador tiene que pedir el OTP y llamar `verificarSegundoFactorRemoto`.
    if (res.ok && datos.requiere_2fa) {
      return { disponible: true, ok: true, requiere_2fa: true, pista: datos.pista, email, expiresAt: datos.expiresAt };
    }

    if (res.ok && datos.token) {
      guardarSesion(datos);
      return { disponible: true, ok: true, ...datos };
    }

    return {
      disponible: true,
      ok: false,
      status: res.status,
      error: datos.error || datos.message || 'Credenciales incorrectas.',
      lockedUntil: datos.lockedUntil,
    };
  }

  return { disponible: false };
}

/** Guarda la sesión de administración emitida por el backend (token firmado + rol + vencimiento). */
function guardarSesion(datos) {
  guardar(KEY_TOKEN, datos.token);
  if (datos.exp) guardar(KEY_EXP, datos.exp);
  if (datos.role) guardar(KEY_ROLE, datos.role);
}

/**
 * Login de administración con la cuenta de GOOGLE — «una sola clave para toda la suite» (dueño,
 * 2026-09-06). `credential` es el `id_token` del botón de Google Identity Services; el backend lo
 * verifica contra el Client ID de la suite y, si el correo está en `admin_users`, emite el MISMO
 * token firmado que `/admin-login`. Contrato de retorno idéntico a `loginAdminRemoto`:
 *   · `disponible:false` → sin backend / sin admin provisionado → el llamador sigue su flujo.
 *   · `disponible:true, ok:false` → el servidor RECHAZÓ (no es admin, token ajeno, bloqueada).
 *   · `requiere_2fa` → la fila exige segundo factor: el OTP ya salió, falta el código.
 */
export async function loginAdminGoogleRemoto(credential) {
  if (typeof fetch !== 'function') return { disponible: false };

  for (const base of destinos()) {
    let res;
    let texto;
    try {
      res = await fetch(`${base}/admin-login/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      texto = await res.text();
    } catch (_) {
      continue;
    }

    if (res.status === 404 || res.status === 502 || res.status === 503 || res.status === 504) continue;

    let datos;
    try { datos = texto ? JSON.parse(texto) : {}; } catch (_) { continue; }

    if (res.ok && datos.requiere_2fa) {
      return { disponible: true, ok: true, requiere_2fa: true, pista: datos.pista, email: datos.email, expiresAt: datos.expiresAt };
    }

    if (res.ok && datos.token) {
      guardarSesion(datos);
      return { disponible: true, ok: true, ...datos };
    }

    return {
      disponible: true,
      ok: false,
      status: res.status,
      error: datos.error || datos.message || 'Esta cuenta de Google no es administradora.',
    };
  }

  return { disponible: false };
}

/**
 * Sesión FIRMADA de cliente con Google (`POST /sesion/google`): Solar Hub verifica la cuenta y firma
 * con su llave propia, rol `client`. Es lo que permite al técnico inscribirse sin ser admin.
 *
 * @returns {Promise<{disponible:boolean, ok?:boolean, token?:string, error?:string}>}
 *   `disponible:false` → no está en servicio (404 sin llave, canal caído): el login sigue como siempre.
 */
export async function sesionClienteGoogleRemoto(credential, name) {
  if (typeof fetch !== 'function') return { disponible: false };
  for (const base of destinos()) {
    let res;
    let datos;
    try {
      res = await fetch(`${base}/sesion/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, name }),
      });
      datos = await res.json().catch(() => ({}));
    } catch (_) {
      continue;
    }
    if (res.status === 404 || res.status === 502 || res.status === 503 || res.status === 504) continue;
    if (res.ok && datos.token) return { disponible: true, ok: true, token: datos.token };
    return { disponible: true, ok: false, error: datos.error || 'No se pudo verificar la cuenta de Google.' };
  }
  return { disponible: false };
}

/**
 * Segundo paso del login: manda el código OTP que el backend envió por WhatsApp tras una clave
 * correcta. Con el código válido, el backend emite el token firmado — el MISMO que habría dado un
 * login de un paso, así que se guarda igual. Contrato de retorno idéntico a `loginAdminRemoto`.
 */
export async function verificarSegundoFactorRemoto(email, code) {
  if (typeof fetch !== 'function') return { disponible: false };

  for (const base of destinos()) {
    let res;
    let texto;
    try {
      res = await fetch(`${base}/admin-verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      texto = await res.text();
    } catch (_) {
      continue;
    }

    if (res.status === 404 || res.status === 502 || res.status === 503 || res.status === 504) continue;

    let datos;
    try { datos = texto ? JSON.parse(texto) : {}; } catch (_) { continue; }

    if (res.ok && datos.token) {
      guardarSesion(datos);
      return { disponible: true, ok: true, ...datos };
    }

    return {
      disponible: true,
      ok: false,
      status: res.status,
      error: datos.error || datos.message || 'Código incorrecto.',
    };
  }

  return { disponible: false };
}

/** El token de administración guardado, si no venció. */
export function tokenAdmin() {
  const t = leer(KEY_TOKEN);
  if (!t) return '';
  const exp = Number(leer(KEY_EXP) || 0);
  if (exp && Date.now() / 1000 > exp) { cerrarAdmin(); return ''; }
  return t;
}

export const rolAdmin = () => leer(KEY_ROLE);

// Espejo del `KEY` de `auth.js`. NO se importa a propósito: `auth.js` ya importa este módulo
// (`loginAdminRemoto`, `cerrarAdmin`), así que traerlo de vuelta sería un ciclo. La duplicación la
// vigila `admin-cerrado.test.js`, que exige que las dos constantes digan lo mismo.
const KEY_SESION_SUITE = 'fluxai.jwt';

/**
 * El token con el que se le habla al hub-api. **Prefiere el de ADMINISTRACIÓN**, que es el único
 * FIRMADO por el backend.
 *
 * 🔴 Por qué existe (medido el 2026-08-29): los llamadores mandaban `getToken()`, o sea el
 * `fluxai.jwt` que acuña `mintToken` — un JWT **`alg:none`**. Mientras `exigeAdmin` estuvo inerte
 * eso pasaba (cualquier cadena de ≥10 caracteres pasaba: `Bearer aaaaaaaaaaaaaaaa` → 200 en
 * producción). Con el guard cerrado, un token sin firma es 401: si nadie manda el firmado, el
 * dueño provisiona su admin, inicia sesión… y el panel sigue sin funcionar. `tokenAdmin()` existía
 * desde F9 y **no tenía un solo llamador** — se guardaba el token bueno y se mandaba el malo.
 */
export function tokenParaBackend() {
  return tokenAdmin() || leer(KEY_SESION_SUITE);
}

export function cerrarAdmin() {
  borrar(KEY_TOKEN); borrar(KEY_EXP); borrar(KEY_ROLE);
}

/** Confirma contra el servidor que el token sigue siendo válido. `null` = no se pudo saber. */
export async function verificarAdmin() {
  const token = tokenAdmin();
  if (!token || typeof fetch !== 'function') return null;
  for (const base of destinos()) {
    try {
      const res = await fetch(`${base}/admin-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ token }),
      });
      if (res.status === 404 || res.status >= 502) continue;
      if (res.status === 401) { cerrarAdmin(); return false; }
      if (res.ok) return true;
    } catch (_) { continue; }
  }
  return null;
}
